'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { ExternalLink, Loader2, Check, UserCog, Lock, Eye, EyeOff } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { joinTournament } from '@/lib/tournaments-data'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { AuthModal } from '@/components/auth/AuthModal'
import { ctaFor, trackExternalTournamentClick } from './detail-common'
import { ApexLinkPrompt, fetchApexLink } from './apex-link-prompt'
import { TournamentNextSteps } from './next-steps'
import { APEX_GAME_NAME } from '@/lib/apex-candidate'
import type { Tournament } from '@/lib/tournaments'

export function JoinTournamentButton({
  t,
  size = 'lg',
  className,
  joinAsSubstitute = false,
  standing = 'none',
  hasBracket = false,
  onMembershipChange,
  autoStart = false,
}: {
  t: Tournament
  size?: 'default' | 'sm' | 'lg'
  className?: string
  /** When confirmed spots are full, a new joiner registers as a substitute. */
  joinAsSubstitute?: boolean
  /** The current user's standing, so the joined label can reflect it. */
  standing?: 'none' | 'confirmed' | 'substitute'
  /** A hosted bracket exists — the completed-state CTA links to it. */
  hasBracket?: boolean
  /** Fires after any successful join/leave so dependent views (chat) can refresh. */
  onMembershipChange?: () => void
  /**
   * Start joining as soon as this mounts (a list card's "Join" opens the event
   * page with ?join=1). Pass it only once the caller's standing has loaded, so
   * an existing registration is never mistaken for "not joined".
   */
  autoStart?: boolean
}) {
  const { session } = useAuth()
  const userId = session?.user?.id ?? null
  const cta = ctaFor(t, { hasBracket })
  const pathname = usePathname()
  const localeSeg = pathname?.split('/')[1]
  const locale = localeSeg === 'es' ? 'es' : localeSeg === 'ja' ? 'ja' : 'en'

  // In-app results CTA (e.g. "View Results") — same-tab next/link navigation.
  if (cta.href && cta.internal) {
    return (
      <Button asChild size={size} className={className}>
        <Link href={`/${locale}${cta.href}`}>{cta.label}</Link>
      </Button>
    )
  }

  // External register / view-results CTAs are plain links — unchanged behaviour.
  if (cta.href) {
    return (
      <Button asChild size={size} className={className}>
        <a
          href={cta.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackExternalTournamentClick(t, 'join_button', cta.href)}
        >
          {cta.label}
          {cta.external && <ExternalLink className="size-4" />}
        </a>
      </Button>
    )
  }

  // Anything that isn't join/waitlist and has no link (e.g. completed with no
  // bracket) is non-interactive.
  const joinable = cta.kind === 'join' || cta.kind === 'waitlist'
  if (!joinable) {
    return (
      <Button size={size} className={className} disabled>
        {cta.label}
      </Button>
    )
  }

  return (
    <JoinableButton
      t={t}
      cta={cta}
      userId={userId}
      size={size}
      className={className}
      joinAsSubstitute={joinAsSubstitute}
      standing={standing}
      onMembershipChange={onMembershipChange}
      autoStart={autoStart}
    />
  )
}

function JoinableButton({
  t,
  cta,
  userId,
  size,
  className,
  joinAsSubstitute,
  standing,
  onMembershipChange,
  autoStart,
}: {
  t: Tournament
  cta: ReturnType<typeof ctaFor>
  userId: string | null
  size: 'default' | 'sm' | 'lg'
  className?: string
  joinAsSubstitute: boolean
  standing: 'none' | 'confirmed' | 'substitute'
  onMembershipChange?: () => void
  autoStart: boolean
}) {
  const { user, loading: authLoading } = useAuth()
  const pathname = usePathname()
  const localeSeg = pathname?.split('/')[1]
  const locale = localeSeg === 'es' ? 'es' : localeSeg === 'ja' ? 'ja' : 'en'

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showAuth, setShowAuth] = useState(false)

  // Password-protected private event: the join RPC refuses a passwordless
  // attempt with `invalid_password`, and that is our cue to ask. We always try
  // without one first, so organizers and invitees (whom Postgres lets through)
  // never see the box.
  const [askPassword, setAskPassword] = useState(false)
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  // After joining an Apex event, ask for an in-game ID if we don't have one.
  // This is a follow-up to a join that already succeeded, never a gate: it
  // renders only once registration is confirmed and can always be dismissed.
  const [askApex, setAskApex] = useState(false)
  const [apexTarget, setApexTarget] = useState<{
    userGameId: string | null
    gameId: string | null
  } | null>(null)

  // "Am I registered" comes from the parent's live standing (single source of
  // truth). `optimistic` briefly overrides it right after a join/leave; it's
  // cleared whenever a fresh standing arrives.
  const [optimistic, setOptimistic] = useState<boolean | null>(null)
  // Set only by a join made in this view, so returning registrants don't get
  // the next-steps panel on every visit.
  const [justJoined, setJustJoined] = useState(false)
  const joined = optimistic ?? standing !== 'none'
  useEffect(() => {
    setOptimistic(null)
  }, [standing])

  // Registration goes through the `join_tournament` RPC: for a password-
  // protected event the INSERT policy refuses direct rows, and the password is
  // verified against the hash inside Postgres. `uid` is only used to gate the
  // call — the RPC registers whoever `auth.uid()` resolves to.
  const joinWithUid = useCallback(
    async (_uid: string, pwd: string | null = null) => {
      setBusy(true)
      setError(null)
      const res = await joinTournament(t.id, { password: pwd })
      if (res.wrongPassword) {
        setAskPassword(true)
        if (pwd) setError('That password is not right. Check the link you were sent.')
      } else if (res.error) {
        setError(res.error)
      } else {
        setAskPassword(false)
        setPassword('')
        setShowPassword(false)
        setOptimistic(true)
        setJustJoined(true)
        onMembershipChange?.()
        // Apex events only, and only when they haven't linked an ID yet. Runs
        // after the join so a lookup failure can never cost them their spot.
        if (t.gameSlug === APEX_GAME_NAME) {
          const link = await fetchApexLink(_uid)
          if (link.state === 'missing') {
            setApexTarget({ userGameId: link.userGameId, gameId: link.gameId })
            setAskApex(true)
          }
        }
      }
      setBusy(false)
    },
    [t.id, t.gameSlug, onMembershipChange]
  )

  const submitPassword = (e: React.FormEvent) => {
    e.preventDefault()
    if (!userId || !password.trim() || busy) return
    joinWithUid(userId, password)
  }

  const leave = useCallback(async () => {
    if (!userId) return
    setBusy(true)
    setError(null)
    const { error } = await supabase
      .from('tournament_registrations')
      .delete()
      .eq('tournament_id', t.id)
      .eq('user_id', userId)
    if (error) {
      setError(error.message)
    } else {
      setOptimistic(false)
      setJustJoined(false)
      setAskApex(false)
      onMembershipChange?.()
    }
    setBusy(false)
  }, [userId, t.id, onMembershipChange])

  const onClick = () => {
    if (!userId) {
      setShowAuth(true)
      return
    }
    if (joined) leave()
    else joinWithUid(userId)
  }

  // Arrived from a list card's "Join": run the same click once, after auth has
  // settled. Signed out → the sign-in modal (which joins on success); no
  // gamertag yet → the "Complete profile" button below; already in → nothing.
  const autoStarted = useRef(false)
  useEffect(() => {
    if (!autoStart || autoStarted.current || authLoading) return
    autoStarted.current = true
    if (joined) return
    if (!userId) {
      setShowAuth(true)
      return
    }
    if (user?.gamertag) joinWithUid(userId)
  }, [autoStart, authLoading, joined, userId, user?.gamertag, joinWithUid])

  // After a fresh sign-in, resolve the current uid and join immediately.
  const handleAuthenticated = useCallback(async () => {
    const { data } = await supabase.auth.getSession()
    const uid = data.session?.user?.id
    if (uid) joinWithUid(uid)
  }, [joinWithUid])

  const joinedLabel =
    standing === 'substitute' ? "You're a substitute" : "You're registered"
  const joinLabel = joinAsSubstitute ? 'Join as Substitute' : cta.label

  // Signed in but hasn't finished onboarding (no gamertag yet): they must
  // complete their profile before joining.
  const needsOnboarding = !!userId && !authLoading && !joined && !user?.gamertag
  if (needsOnboarding) {
    return (
      <div className={className}>
        <Button asChild size={size} className="w-full sm:w-auto">
          <Link href={`/${locale}/app/onboarding`}>
            <UserCog className="size-4" />
            Complete profile to join
          </Link>
        </Button>
      </div>
    )
  }

  if (askPassword && !joined && userId) {
    return (
      <div className={className}>
        <form onSubmit={submitPassword} className="flex w-full flex-col gap-2">
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Lock className="size-3.5 shrink-0" />
            This event needs a password to join.
          </p>
          <div className="flex w-full items-center gap-2">
            <div className="relative min-w-0 flex-1 animate-in fade-in-0 slide-in-from-right-4 duration-300 ease-out">
              <input
                type={showPassword ? 'text' : 'password'}
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Event password"
                aria-label="Event password"
                className="h-10 w-full rounded-xl border border-border bg-background pl-3.5 pr-10 text-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-primary"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
                tabIndex={-1}
                className="absolute right-1 top-1/2 -translate-y-1/2 rounded-lg p-2 text-muted-foreground transition-colors hover:text-foreground"
              >
                {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </button>
            </div>
            <Button type="submit" size={size} disabled={!password.trim() || busy}>
              {busy ? <Loader2 className="size-4 animate-spin" /> : null}
              <span className="truncate">{joinLabel}</span>
            </Button>
          </div>
          <button
            type="button"
            onClick={() => {
              setAskPassword(false)
              setPassword('')
              setShowPassword(false)
              setError(null)
            }}
            className="self-start text-xs text-muted-foreground underline-offset-2 transition-colors hover:text-foreground hover:underline"
          >
            Cancel
          </button>
          {error && <p className="text-xs text-red-400">{error}</p>}
        </form>
      </div>
    )
  }

  // Password first (if any), then this — same slot, so the join flow reads as
  // one continuous step rather than a modal stack.
  if (askApex && userId) {
    return (
      <div className={className}>
        <ApexLinkPrompt
          tournamentId={t.id}
          locale={locale}
          userId={userId}
          userGameId={apexTarget?.userGameId ?? null}
          gameId={apexTarget?.gameId ?? null}
          onLinked={() => setAskApex(false)}
          onDismiss={() => setAskApex(false)}
        />
      </div>
    )
  }

  return (
    <div className={className}>
      <div className="flex w-full items-center gap-2">
        <Button
          onClick={onClick}
          disabled={busy}
          size={size}
          variant={joined ? 'outline' : 'default'}
          className="min-w-0 flex-1 sm:flex-none"
        >
          {busy ? (
            <Loader2 className="size-4 animate-spin" />
          ) : joined ? (
            <Check className="size-4" />
          ) : null}
          <span className="truncate">{joined ? joinedLabel : joinLabel}</span>
        </Button>
        {joined && !busy && (
          <button
            onClick={leave}
            className="shrink-0 text-xs text-muted-foreground underline-offset-2 transition-colors hover:text-foreground hover:underline"
          >
            Leave
          </button>
        )}
      </div>
      {error && <p className="mt-2 text-xs text-red-400">{error}</p>}

      {justJoined && joined && userId && (
        <TournamentNextSteps
          t={t}
          userId={userId}
          gamertag={user?.gamertag ?? null}
          locale={locale}
        />
      )}

      <AuthModal
        open={showAuth}
        onClose={() => setShowAuth(false)}
        onAuthenticated={handleAuthenticated}
        defaultMode="signup"
        subtitle={
          cta.kind === 'waitlist'
            ? 'Log in or create an account to join the waitlist.'
            : 'Log in or create an account to join this tournament.'
        }
      />
    </div>
  )
}
